import {
  Checkbox,
  Table,
  ButtonIcon,
  useToast,
  ButtonText,
  FormFeedback
} from '@morressier/alembic';
import React, { useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { Box, Tooltip, Flex } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FieldLabel } from 'src/components/Field';
import { get } from 'lodash';
import { NewKeywordModal } from './NewKeywordModal';
import SpecialtyKeywordTableRow, {
  getConfigProperties
} from './SpecialtyKeywordTableRow';
import { exactTextCheck, toTitleCase } from './signupHook';
import { FormFieldSelect as IFormFieldSelect } from '@publishing-engine/domain/types/article';

export type ISuggestedResponse = {
  termId: string;
  name: string;
};

export type SpecialtyKeywordRow = {
  specialty?: ISuggestedResponse | null;
  subSpecialty?: ISuggestedResponse | null;
  keyword?: ISuggestedResponse | null;
  scandinavianKeyword?: ISuggestedResponse | null;
  isCustomKeyword?: boolean;
};

export type SpecialtyKeywordConfigType = {
  Specialty: IFormFieldSelect;
  SubSpecialty?: IFormFieldSelect | null;
  Keyword: IFormFieldSelect;
  ScandinavianKeyword?: IFormFieldSelect | null;
};

export const MAX_ROW_LIMIT = 10;

const formatToastText = (text: string, maxLength: number = 30): string => {
  if (text.length > maxLength) {
    const truncatedText = `${text.slice(0, maxLength)}...`;
    return truncatedText;
  }

  return text;
};

const normalizeText = (value?: string) =>
  exactTextCheck(value || '').toLowerCase();

const getRowFieldPaths = (
  fieldPrefix: string,
  rowIndex: number,
  configs: SpecialtyKeywordConfigType
) =>
  [
    `${fieldPrefix}.${rowIndex}.specialty`,
    configs.SubSpecialty ? `${fieldPrefix}.${rowIndex}.subSpecialty` : null,
    `${fieldPrefix}.${rowIndex}.keyword`,
    configs.ScandinavianKeyword
      ? `${fieldPrefix}.${rowIndex}.scandinavianKeyword`
      : null
  ].filter(Boolean) as string[];

const hasUniformRowValue = (row?: SpecialtyKeywordRow | null) => {
  if (!row) return false;

  const values = [
    row.specialty?.name,
    row.subSpecialty?.name,
    row.keyword?.name,
    row.scandinavianKeyword?.name
  ]
    .map(value => exactTextCheck(value ?? ''))
    .filter(Boolean);

  return values.length >= 2 && new Set(values).size === 1;
};

const DOWNSTREAM_FIELDS: Record<
  string,
  Array<'subSpecialty' | 'keyword' | 'scandinavianKeyword'>
> = {
  // Only Specialty cascades to Sub-Specialty. Keyword and Scandinavian
  // Keyword are user-added (free text), so they are intentionally kept
  // as-is when a parent changes.
  specialty: ['subSpecialty']
};

const CONFIG_KEY_BY_FIELD: Record<
  string,
  'SubSpecialty' | 'Keyword' | 'ScandinavianKeyword'
> = {
  subSpecialty: 'SubSpecialty',
  keyword: 'Keyword',
  scandinavianKeyword: 'ScandinavianKeyword'
};

const SpecialtyKeywordTable: React.FC<{
  configs: SpecialtyKeywordConfigType;
}> = ({ configs }) => {
  const toast = useToast();
  const {
    control,
    watch,
    setValue,
    setError,
    formState: { errors },
    trigger,
    clearErrors
  } = useFormContext();
  const fieldName = 'researchFocusAndExpertise.specialtyAndKeywords' as const;
  const error = get(errors, fieldName);
  const errorMessage = error?.root?.message as string;
  // Array of rows: researchSpecialtyKeywordRows[index]
  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldName
  });

  const [selected, setSelected] = useState<boolean[]>([]);
  const [isNewKeywordModalOpen, setIsNewKeywordModalOpen] = useState(false);
  const [currentRowIndex, setCurrentRowIndex] = useState<number | null>(null);
  const [selectedField, setSelectedField] = useState('');
  const [selectedFieldLabel, setSelectedFieldLabel] = useState('');

  const isKeywordOptionDisabled = () => false;

  useEffect(() => {
    setSelected(prev => {
      if (fields.length === prev.length) return prev;
      if (fields.length > prev.length) {
        return [...prev, ...Array(fields.length - prev.length).fill(false)];
      }
      return prev.slice(0, fields.length);
    });
  }, [fields.length]);

  const toggleRow = (index: number) => {
    setSelected(prev => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allChecked = selected.length > 0 && selected.every(v => v);
    setSelected(Array(fields.length).fill(!allChecked));
  };

  const handleAddRow = async () => {
    append({
      specialty: null,
      subSpecialty: null,
      keyword: null,
      scandinavianKeyword: null,
      isCustomKeyword: false
    } as SpecialtyKeywordRow);
  };

  const handleDeleteSelected = () => {
    const indicesToRemove = selected
      .map((isSelected, index) => (isSelected ? index : -1))
      .filter(index => index !== -1)
      .sort((a, b) => b - a);

    if (indicesToRemove.length === 0) return;

    indicesToRemove.forEach(index => remove(index));
    toast({
      variant: 'success',
      title: `${indicesToRemove?.length} row(s) deleted`
    });
  };

  const handleDeleteRow = (index: number) => {
    clearErrors(`${fieldName}.${index}`);
    toast({
      variant: 'success',
      title: `Row ${index + 1} has been deleted successfully`
    });
    remove(index);
  };

  // Re-selecting the same option is a no-op, so it must not clear what the
  // user already entered for the dependent fields.
  const selectionChanged = (previous: any, next: any) =>
    get(previous, 'termId') !== get(next, 'termId');

  // Clear downstream cells whenever a parent changes, so a stale
  // Sub-Specialty never outlives the Specialty it was picked for.
  const resetDownstreamFields = (rowIndex: number, fieldKey: string) => {
    (DOWNSTREAM_FIELDS[fieldKey] ?? []).forEach(downstreamField => {
      const configKey = CONFIG_KEY_BY_FIELD[downstreamField];
      if (!configKey || !configs[configKey]) return;

      const downstreamPath = `${fieldName}.${rowIndex}.${downstreamField}`;
      if (watch(downstreamPath)) {
        setValue(downstreamPath, null, {
          shouldDirty: true,
          shouldValidate: false
        });
      }
    });
  };

  const handleNewKeywordClick = (
    rowIndex: number,
    fieldName: string,
    fieldLabel?: string
  ) => {
    setCurrentRowIndex(rowIndex);
    setIsNewKeywordModalOpen(true);
    setSelectedField(fieldName);
    setSelectedFieldLabel(toTitleCase(fieldLabel || fieldName));
  };

  const handleSaveCustomKeyword = (keywordName: string) => {
    if (currentRowIndex === null) return;

    const fieldPath = `${fieldName}.${currentRowIndex}.${selectedField}`;
    const rowPath = `${fieldName}.${currentRowIndex}`;
    const rowPaths = getRowFieldPaths(fieldName, currentRowIndex, configs);

    const normalizedKeyword = normalizeText(keywordName);
    const customKeywordId = `custom_${selectedField}_${normalizedKeyword.replace(
      /\s+/g,
      '_'
    )}`;

    const previousValue = watch(fieldPath);

    clearErrors(fieldPath);
    setValue(
      fieldPath,
      {
        termId: customKeywordId,
        name: keywordName,
        isCustom: true
      },
      { shouldDirty: true, shouldValidate: true }
    );

    if (selectionChanged(previousValue, { termId: customKeywordId })) {
      resetDownstreamFields(currentRowIndex, selectedField);
    }

    const currentRow = watch(rowPath) as SpecialtyKeywordRow;
    const nextRow: SpecialtyKeywordRow = {
      ...currentRow,
      [selectedField]: {
        termId: customKeywordId,
        name: keywordName,
        isCustom: true
      }
    };

    clearErrors(rowPaths as any);
    if (hasUniformRowValue(nextRow)) {
      setError(fieldPath, {
        type: 'validate',
        message:
          'Duplicate Research Specialty & Keyword combination is not allowed. Please modify at least one field.'
      });
      return;
    }

    toast({
      variant: 'success',
      title: formatToastText(keywordName),
      description: `added as a new ${selectedFieldLabel || selectedField}`
    });
    setIsNewKeywordModalOpen(false);
  };

  const handleCellChange = async (fieldName: string, value: any) => {
    const previousValue = watch(fieldName);
    setValue(fieldName, value, { shouldDirty: true, shouldValidate: true });

    const match = fieldName.match(
      /researchFocusAndExpertise.specialtyAndKeywords\.(\d+)\.(specialty|subSpecialty|keyword|scandinavianKeyword)/
    );
    if (!match) return;

    const [, rowIndexStr] = match;
    const rowIndex = parseInt(rowIndexStr || '', 10);
    const fieldKey = match[2] as
      | 'specialty'
      | 'subSpecialty'
      | 'keyword'
      | 'scandinavianKeyword';
    const rowPaths = getRowFieldPaths(
      'researchFocusAndExpertise.specialtyAndKeywords',
      rowIndex,
      configs
    );

    if (selectionChanged(previousValue, value)) {
      resetDownstreamFields(rowIndex, fieldKey);
    }

    clearErrors(rowPaths as any);
    await trigger(fieldName);

    const currentRow = watch(
      `researchFocusAndExpertise.specialtyAndKeywords.${rowIndex}`
    ) as SpecialtyKeywordRow;
    const nextRow: SpecialtyKeywordRow = {
      ...currentRow,
      [fieldKey]: value
    };

    if (hasUniformRowValue(nextRow)) {
      setError(fieldName, {
        type: 'validate',
        message:
          'Duplicate Research Specialty & Keyword combination is not allowed. Please modify at least one field.'
      });
      return;
    }

    clearErrors(fieldName);
  };

  const allChecked = fields.length > 0 && selected.every(v => v);
  const isIndeterminate = selected.some(v => v) && !allChecked;

  const renderHeaderCell = (config: IFormFieldSelect) => {
    const { validations, labels, name } = getConfigProperties(config);

    return (
      <Table.HeaderCell flex={1} data-cy="header-cell">
        <FieldLabel
          label={labels.main}
          isRequired={name === 'Specialty' ? true : validations.required}
          subText=""
          helpText={labels.subtitle}
        />
      </Table.HeaderCell>
    );
  };

  const isMaxRowLimit = fields?.length === MAX_ROW_LIMIT;
  const tableMinWidth =
    configs.SubSpecialty || configs.ScandinavianKeyword ? '960px' : '560px';
  const newKeywordModalHeading =
    selectedField === 'subSpecialty'
      ? 'Create new Sub-Specialty'
      : selectedField === 'scandinavianKeyword'
      ? 'Create new Scandinavian keyword'
      : `Create new ${
          selectedField.charAt(0).toUpperCase() + selectedField.slice(1)
        }`;

  return (
    <Flex direction={'column'} id="SpecialtyKeywords" mb={5}>
      <FieldLabel
        label="Research Specialty & Keyword(s)"
        isRequired={false}
        subText=""
      />

      {fields.length === 0 ? (
        <Tooltip
          placement="auto"
          hasArrow
          label="Click on 'Add Specialty & Keyword(s)' to add specialty & keyword(s)"
        >
          <Box width={'fit-content'}>
            <ButtonText
              variant="secondary"
              onClick={handleAddRow}
              width="fit-content"
              data-cy="add-specialty-keyword-button"
              iconLeft={<FontAwesomeIcon icon={faPlus} />}
              mt={3}
            >
              Add Specialty & Keyword(s)
            </ButtonText>
          </Box>
        </Tooltip>
      ) : (
        <Box overflowX="auto">
          <Box minWidth={tableMinWidth}>
            <Table data-cy="table">
              <Table.HeaderRow
                display="flex"
                alignItems="center"
                data-cy="table-header"
                gap={4}
                minWidth="100%"
              >
                <Table.HeaderCell>
                  <Checkbox
                    ariaLabel="Select All"
                    id="check-all"
                    name="check-all"
                    isChecked={allChecked}
                    isIndeterminate={isIndeterminate}
                    onChange={toggleSelectAll}
                  >
                    <Box as="span" srOnly>
                      Select all rows
                    </Box>
                  </Checkbox>
                </Table.HeaderCell>
                {renderHeaderCell(configs.Specialty)}
                {configs.SubSpecialty && renderHeaderCell(configs.SubSpecialty)}
                {renderHeaderCell(configs.Keyword)}
                {configs.ScandinavianKeyword &&
                  renderHeaderCell(configs.ScandinavianKeyword)}
                <Table.HeaderCell width={5} data-cy="header-cell">
                  <Tooltip label="Add row" placement="top" hasArrow>
                    <Box>
                      <ButtonIcon
                        ariaLabel="Add row"
                        icon={<FontAwesomeIcon icon={faPlus} />}
                        data-cy="plus-icon"
                        ml={1}
                        onClick={handleAddRow}
                        disabled={isMaxRowLimit}
                      />
                    </Box>
                  </Tooltip>
                </Table.HeaderCell>
                <Table.HeaderCell width={10} data-cy="header-cell">
                  <Tooltip label="Delete row(s)" placement="top" hasArrow>
                    <Box>
                      <ButtonText
                        aria-label="Delete row(s)"
                        variant="caution"
                        data-cy="delete-icon"
                        ml={1}
                        onClick={handleDeleteSelected}
                        isDisabled={!selected.some(v => v)}
                      >
                        <FontAwesomeIcon icon={faTrashCan} />
                      </ButtonText>
                    </Box>
                  </Tooltip>
                </Table.HeaderCell>
              </Table.HeaderRow>

              <Table.Body
                data-cy="table-body"
                maxH={300}
                overflowY={'auto'}
                overflowX="hidden"
              >
                {fields.map((field, index) => (
                  <SpecialtyKeywordTableRow
                    handleAddRow={handleAddRow}
                    handleCellChange={handleCellChange}
                    isKeywordOptionDisabled={isKeywordOptionDisabled}
                    fieldName={fieldName}
                    handleDeleteRow={handleDeleteRow}
                    handleNewKeywordClick={handleNewKeywordClick}
                    index={index}
                    rowField={field}
                    selected={selected}
                    toggleRow={toggleRow}
                    key={field.id}
                    configs={configs}
                    isMaxRowLimit={isMaxRowLimit}
                  />
                ))}
                {errorMessage && (
                  <FormFeedback variant="error">{errorMessage}</FormFeedback>
                )}
              </Table.Body>
            </Table>
          </Box>
        </Box>
      )}
      <NewKeywordModal
        isOpen={isNewKeywordModalOpen}
        onClose={() => setIsNewKeywordModalOpen(false)}
        onSave={handleSaveCustomKeyword}
        selectedField={selectedFieldLabel || selectedField}
        heading={newKeywordModalHeading}
        defaultValue={''}
        specialtyName={
          currentRowIndex !== null
            ? watch(`${fieldName}.${currentRowIndex}.specialty`)?.name
            : undefined
        }
        subHeading={
          selectedField === 'scandinavianKeyword'
            ? 'Describe your area of expertise.'
            : undefined
        }
        placeholderText={
          selectedField === 'scandinavianKeyword'
            ? 'e.g., Nursing Education'
            : undefined
        }
      />
    </Flex>
  );
};

export default SpecialtyKeywordTable;
